import React, { useEffect, useState } from "react";
import AddLabelModal from "../../components/modal/AddLabelModal"; // Adjust path
import EditLabelModal from "../../components/modal/EditLabelModal"; // Add this import
import AddNewArtistModal from "../../components/modal/AddNewArtistModal";
import Swal from "sweetalert2";
import { adminApi } from "../../repositories/admin";
import { PencilIcon } from "@heroicons/react/24/outline"; // Importing icons
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import { dataTableThClass, dataTableRowClass, dataTableTdClass } from "../../components/ui/DataTable";
import RowActionsMenu, { RowMenuItem } from "../../components/ui/RowActionsMenu";
import { cn } from "../../DashboardPage/lib/utils";
import Button from "../../components/ui/Button";
import { SkeletonTableRows } from "../../components/ui/skeleton";

interface Label {
  id: number;
  name: string;
  type: string;
  availableDSPs: string[];
  availableAnalyticsDSPs?: string[];
}

const Labels = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddArtistModalOpen, setIsAddArtistModalOpen] = useState(false);
  const [labels, setLabels] = useState<Label[]>([]);
  // Starts true: until the first response the table shows placeholder rows,
  // not the "No clients found" empty state.
  const [loading, setLoading] = useState(true);
  const [currentLabel, setCurrentLabel] = useState<Label | null>(null);
  const [selectedLabelId, setSelectedLabelId] = useState<number | null>(null);
  const [userRole, setUserRole] = useState<string>("");

  const fetchLabels = async () => {
    try {
      const response = await adminApi.getLabels();
      setLabels(response.data.data);
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to fetch labels",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id: number) => {
    const labelToEdit = labels.find((label) => label.id === id);
    if (labelToEdit) {
      setCurrentLabel(labelToEdit);
      setIsEditModalOpen(true);
    }
  };

  const handleAddArtist = (label: Label) => {
    const role = JSON.parse(localStorage.getItem("auth") || "{}")?.user?.role;
    setUserRole(role);
    setSelectedLabelId(label.id);
    setCurrentLabel(label);
    setIsAddArtistModalOpen(true);
  };

  const handleSaveEdit = async (name: string, type: string, availableDSPs: string[], availableAnalyticsDSPs: string[]) => {
    if (!currentLabel) {
      return Swal.fire("Error", "No label selected for editing", "error");
    }
    try {
      const {data} = await adminApi.updateLabel(currentLabel.id, {
        name,
        type,
        availableDSPs,
        availableAnalyticsDSPs
      });
      if(data?.success) {
        Swal.fire("Success", "Label updated successfully", "success");
        setLabels((prev) => prev.map((label) =>
          label.id === currentLabel.id
            ? {
                ...label,
                name,
                type,
                availableDSPs: availableDSPs,
                availableAnalyticsDSPs: availableAnalyticsDSPs
              }
            : label
        ));
      }
      setIsEditModalOpen(false);
    } catch (error: any) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to update label",
        "error"
      );
    }
  };

  useEffect(() => {
    fetchLabels();
  }, []);

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Labels" }]}
        className="mb-6"
        title="Client Management"
        description="Client companies, their DSP access, and their artists."
        actions={
          <Button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto">
            Add Company
          </Button>
        }
      />

      {!loading && labels.length === 0 ? (
        <div className="py-12 text-center bg-white border rounded-lg border-neutral-200">
          <p className="text-sm font-medium text-black">No clients found</p>
          <p className="mt-1 text-sm text-neutral-500">Add a company to get started.</p>
        </div>
      ) : (
        <div
          className="overflow-x-auto bg-white border rounded-lg border-neutral-200"
          aria-busy={loading}
        >
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="border-b border-neutral-200 bg-primary-50">
                <th className={dataTableThClass}>Id</th>
                <th className={dataTableThClass}>Name</th>
                <th className={dataTableThClass}>Type</th>
                <th className={dataTableThClass}>DSP Access</th>
                <th className={dataTableThClass}>Analytics DSP Access</th>
                <th className={dataTableThClass}>Add Artists</th>
                <th className={dataTableThClass}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <SkeletonTableRows
                  rows={6}
                  columns={["left", "left", "left", "left", "left", "left", "left"]}
                  cellClassName={dataTableTdClass}
                />
              )}
              {labels.map((label, index) => (
                <tr key={label.id} className={dataTableRowClass}>
                  <td className={dataTableTdClass}>{label.id}</td>
                  <td className={cn(dataTableTdClass, "font-medium whitespace-nowrap")}>{label.name}</td>
                  <td className={dataTableTdClass}>{label.type}</td>
                  <td className={dataTableTdClass}>
                    <span className="block max-w-xs" title={label.availableDSPs?.join(", ")}>
                      {label.availableDSPs?.join(", ") || "N/A"}
                    </span>
                  </td>
                  <td className={dataTableTdClass}>
                    <span className="block max-w-xs" title={label.availableAnalyticsDSPs?.join(", ")}>
                      {label.availableAnalyticsDSPs?.join(", ") || "N/A"}
                    </span>
                  </td>
                  <td className={cn(dataTableTdClass, "text-center")}>
                    <Button variant="secondary" size="sm" onClick={() => handleAddArtist(label)}>
                      Add Artist
                    </Button>
                  </td>
                  <td className={cn(dataTableTdClass, "text-center")}>
                    <RowActionsMenu label={`Actions for ${label.name || "company"}`}>
                      <RowMenuItem
                        icon={<PencilIcon className="w-4 h-4" />}
                        onClick={() => handleEdit(label.id)}
                      >
                        Edit
                      </RowMenuItem>
                    </RowActionsMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Company Modal */}
      <AddLabelModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          fetchLabels();
        }}
      />

      {/* Edit Label Modal */}
      {currentLabel && (
        <EditLabelModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          labelId={currentLabel.id}
          initialName={currentLabel.name}
          initialType={currentLabel.type}
          initialAvailableDSPs={currentLabel.availableDSPs|| []}
          initialAvailableAnalyticsDSPs={currentLabel.availableAnalyticsDSPs|| []}
          onSave={handleSaveEdit}
        />
      )}

      {/* Add New Artist Modal */}
      {isAddArtistModalOpen && (
        <AddNewArtistModal
          isOpen={isAddArtistModalOpen}
          onClose={() => setIsAddArtistModalOpen(false)}
          labelId={selectedLabelId}
          userRole={userRole}
        />
      )}
    </div>
  );
};

export default Labels;